import DefaultLink from '@/components/common/default/DefaultLink';
import IndexSkusCarouselSkeleton from '@/components/common/Skeleton/IndexSkusCarouselSkeleton';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkuDTO } from '@/types/api/sku';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { memo, useRef } from 'react';
import { Navigation, Virtual } from 'swiper/modules';
import SkusCommonCarousel from '@/components/common/carousels/SkusCommonCarousel';
import { SwiperProps } from 'swiper/react';

export type IndexSkusCarouselProps = {
  skus: SkuDTO[];
  href?: string;
  title: string;
  isLoading?: boolean;
  isRelease?: boolean;
} & Omit<SwiperProps, 'children'>;

function IndexSkusCarousel({
  skus,
  href,
  title,
  isLoading,
  isRelease = false,
  ...swiperOptions
}: IndexSkusCarouselProps) {
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  const defaultSwiperConfig = {
    ...swiperOptions,
    modules: [Virtual, Navigation, ...(swiperOptions?.modules || [])],
    breakpoints: {
      0: {
        slidesPerView: 1.2,
        spaceBetween: 10,
      },
      350: {
        slidesPerView: 1.7,
        spaceBetween: 10,
      },
      430: {
        slidesPerView: 2.5,
        spaceBetween: 10,
      },
      716: {
        slidesPerView: 3.5,
        spaceBetween: 11,
      },
      886: {
        slidesPerView: 4.5,
        spaceBetween: 12,
      },
      1024: {
        slidesPerView: 4,
        spaceBetween: 25,
      },
    },
    loop: false,
    className:
      '!tw-h-fit max-nine:tw-h-[300px] tw-relative !tw-overflow-x-clip !tw-overflow-y-visible',
    virtual: false,
    navigation: {
      prevEl: prevRef.current,
      nextEl: nextRef.current,
    },
    onBeforeInit: (swiper: any) => {
      if (swiper.params.navigation) {
        swiper.params.navigation.prevEl = prevRef.current;
        swiper.params.navigation.nextEl = nextRef.current;
      }
    },
  };

  if (isLoading) {
    return <IndexSkusCarouselSkeleton />;
  }

  if (!isLoading && skus.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-4 max-nine:tw-px-4">
        <h2 className="tw-relative tw-w-fit tw-font-plusJakartaSans tw-text-2xl tw-font-semibold tw-text-text max-nine:tw-text-lg">
          {title}
          <hr
            aria-hidden="true"
            className="tw-absolute tw-mt-1 tw-h-[5px] tw-w-[80%] tw-rounded-full tw-border-none tw-bg-custom-gold tw-opacity-100 max-nine:tw-h-[3px]"
          />
        </h2>
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-4">
          {href && (
            <DefaultLink
              href={href}
              className="tw-font-plusJakartaSans tw-text-base tw-font-semibold tw-text-textBlack max-nine:tw-text-sm"
            >
              {translateCommon(CommonEnum['view_more'])}
            </DefaultLink>
          )}
        </div>
      </div>
      <SkusCommonCarousel skus={skus} {...defaultSwiperConfig} isRelease={isRelease} />
    </div>
  );
}

export default memo(IndexSkusCarousel);
